import { useMemo, useEffect, type PropsWithChildren } from "react";
import Uppy from "@uppy/core";
import Tus from "@uppy/tus";
import { apiConfig } from "@/shared/config/api";
import { useAllowedExtensions } from "./useAllowedExtensions";
import { UppyContext } from "./UppyContext";
import { useAuth } from "./useAuth";
import { getAccessToken } from "@/shared/lib/auth-token";

interface FileMeta {
  fileId?: string;
  uploadUrl?: string;
  [key: string]: unknown;
}

interface UppyFileWithMeta {
  id: string;
  meta: FileMeta;
  tus?: {
    uploadUrl?: string;
    [key: string]: unknown;
  };
  uploadURL?: string;
  [key: string]: unknown;
}

interface UploadResponse {
  uploadURL?: string;
  [key: string]: unknown;
}

export function UppyProvider({ children }: PropsWithChildren) {
  const { isAuthenticated } = useAuth();
  const { data: allowedExtensions } = useAllowedExtensions({
    enabled: isAuthenticated,
  });

  const uppy = useMemo(() => {
    const instance = new Uppy({
      restrictions: {
        maxNumberOfFiles: 50,
        allowedFileTypes: allowedExtensions?.extensions ?? undefined,
      },
      autoProceed: false,
    });

    instance.use(Tus, {
      endpoint: `${apiConfig.baseUrl}/files`,
      retryDelays: [0, 1000, 3000, 5000],
      chunkSize: 5 * 1024 * 1024,
      limit: 1,
      onBeforeRequest: async (request) => {
        const token = await getAccessToken();
        if (token) {
          request.setHeader("Authorization", `Bearer ${token}`);
        }
      },
    });

    instance.on("upload-success", (file, response) => {
      if (!file) return;

      const fileWithMeta = file as unknown as UppyFileWithMeta;
      const responseData = response as unknown as UploadResponse;
      const uploadUrl =
        responseData?.uploadURL ||
        fileWithMeta.tus?.uploadUrl ||
        fileWithMeta.meta?.uploadUrl;

      if (uploadUrl) {
        const match = uploadUrl.match(/\/files\/([^/?]+)/);
        if (match && match[1]) {
          const fileId = match[1];
          fileWithMeta.meta = {
            ...fileWithMeta.meta,
            fileId: fileId,
          };
        }
      }
    });

    instance.on("upload", () => {
      const checkForUploadUrl = () => {
        const files = instance.getFiles() as unknown as UppyFileWithMeta[];
        files.forEach((file) => {
          if (!file.meta?.fileId) {
            const uploadUrl =
              file.tus?.uploadUrl || file.meta?.uploadUrl || file.uploadURL;

            if (uploadUrl) {
              const match = uploadUrl.match(/\/files\/([^/?]+)/);
              if (match && match[1]) {
                const fileId = match[1];
                file.meta = {
                  ...file.meta,
                  fileId: fileId,
                };
              }
            }
          }
        });
      };

      checkForUploadUrl();
      const intervalId = setInterval(() => {
        checkForUploadUrl();
        const files = instance.getFiles() as unknown as UppyFileWithMeta[];
        if (files.every((f) => f.meta?.fileId)) {
          clearInterval(intervalId);
        }
      }, 100);

      setTimeout(() => clearInterval(intervalId), 5000);
    });

    return instance;
  }, [allowedExtensions?.extensions]);

  useEffect(() => {
    return () => {
      try {
        uppy.cancelAll();
        uppy.getFiles().forEach((file) => {
          uppy.removeFile(file.id);
        });
      } catch {
        // Ignore cleanup errors
      }
    };
  }, [uppy]);

  const value = useMemo(() => ({ uppy }), [uppy]);

  return <UppyContext.Provider value={value}>{children}</UppyContext.Provider>;
}
